// Listing modes for Flight & Duty — List / Duty log, plus the
// flight-admin-only Rules mode. (H267: Limitations merged into the Duty log;
// `limitations` redirects there. Its KPI row became a Dashboard mode, which
// H287 MOVED to the Pilots home's "Flight & Duty" tab: `dashboard` redirects to
// /pilots?tab=flight-duty, PILOTS_FD_TAB_PATH below.) (H212, Aaron 2026-09-30: "merge … canduty and
// … tracker … call the mode duty log": the Crew Tracker is retired INTO the
// Duty log, and `tracker` redirects there — RETIRED_MODE_SEGMENTS below.)
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/bookings/modes.ts, itself mirrored from src/blank/BlankLayout.tsx
// (Rule 0 — the canonical reference).
//
//   <base>/list         → FlightDutyList         (the duty register)
//   <base>/dashboard    → /pilots?tab=flight-duty (moved to the Pilots home, H287)
//   <base>/canduty      → FlightDutyCanduty      (the Duty log: banners with a
//                                                wheel per limitation, the table)
//   <base>/rules        → FlightDutyRules        (flight admins only: which CARs
//                                                rule sets are enabled)
//
// ── THIS MODULE WAS SHIPPED **MINOR** AND IS NOW **MAJOR**. WHY IT CHANGED ──
// FlightDutyList.tsx and index.tsx both carried a reasoned argument for MINOR:
// no Overview, no Layout, no switcher, on the grounds that a KPI surface over
// `hour`/`duty`/`rest` would be a hero metric reading "0.0 hours" — an artefact
// of an unwritten column rather than a measurement. That argument was correct
// and it is NOT what this promotion overturns.
//
// PATTERNS.md is explicit that major/minor is a per-module PRODUCT decision
// Aaron makes, and the MINOR files said so in as many words ("Nobody has made
// that call ... major/minor is Aaron's call to make, not a default"). Aaron has
// now made it: the module gets a view switcher and a limitations view. The
// promotion is that decision landing, not a lane overriding the earlier
// reasoning — and the earlier reasoning is honoured rather than discarded,
// because the Limitations mode renders NO metric it cannot source. See
// FlightDutyLimitations.tsx.
//
// ── THE SLUG IS `flightduty`, THE DIRECTORY IS `flight-duty` ────────────────
// Deliberate, and measured: `flightduty` (no underscore) is the legacy
// mod_function, the v6 router prefix (/v6/flightduty), and the config_function
// of 25+ config rows. App.tsx generates module routes from the live nav row's
// mod_function and resolves them against Features.tsx by that same key, so the
// storage key below follows the SLUG and not the directory name. `flight_duty`
// would name a component nothing can reach.

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import type { ModeGates } from "../app/navGates";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  // No `dashboard`: H267's KPI tiles moved to the Pilots home's "Flight &
  // Duty" tab (H287, Aaron 2026-09-30: "…/app/flightduty/dashboard. move this
  // to a tab here …/app/pilots"). The segment redirects there (index.tsx).
  // "List", not "Register" (Aaron, round 2 P1: "rename the switcher to list so
  // it matches other modules") — 16 of 17 converted modules label their grid
  // mode "List"; see src/flights/modes.ts.
  list: "List",
  // H212: ONE mode for the Crew Tracker and the Duty Log. Every reader gets
  // it: a flight admin sees every pilot's banner, a pilot only their own
  // (FlightDutyCanduty, useFdScope), so it needs no gate. The segment stays
  // `canduty`, the one every saved Duty Log link, the Personnel roster's
  // Flight Duty light (H96 #5a) and `?pilot=` deep link already use.
  // H353 (Aaron 2026-10-04: "Duty Log. should have a capital L").
  canduty: "Duty Log",
  // P4-DAILY (Aaron 2026-10-07: "yes build the new mode with s switcher
  // called Summary"; the operator: "a list of the F&D duty for all pilots
  // for 1 day"): every pilot's Duty Log row for ONE date. Legacy precedent:
  // Flight & Duty › Reports › "Flight & Duty Summary". Gated below.
  summary: "Summary",
};

/**
 * Mode segments this module RETIRED, and the mode that replaced each (H212).
 * A saved URL on one lands on its replacement with its query kept
 * (index.tsx, `RenamedModeRedirect`), and a remembered one resumes there
 * (`readRememberedModeSegment`) instead of silently resetting to the default.
 * Module-local, not `components/renamedModeSegments`' shared map: that map
 * migrates EVERY module's stored mode, and `tracker` is not a retired name
 * anywhere else.
 */
export const RETIRED_MODE_SEGMENTS: Readonly<{ [segment: string]: string }> = {
  tracker: "canduty",
  // H267 (Aaron 2026-09-30: "lets merge …/flightduty/limitations into
  // …/flightduty/canduty"): its limit cards are the Duty log banner's wheels,
  // its recent entries the Duty log table's columns, its KPI row the
  // Dashboard. A saved Limitations link lands on the Duty log.
  limitations: "canduty",
  // H287: the Dashboard moved to the Pilots home. A saved `/dashboard` URL
  // goes THERE (index.tsx), but a REMEMBERED mode is a user opening Flight &
  // Duty from its own menu, so it resumes inside the module, on the Duty log —
  // the mode whose banners carry the same limits — not on another section.
  dashboard: "canduty",
};

/** The module's route slug: its `modules.mod_function`, no underscore. */
export const FLIGHT_DUTY_SLUG = "flightduty";

/** The Pilots home tab that holds the Flight & Duty dashboard (H287), and the
 *  URL that opens the home on it (SectionDashboard reads `?tab=`). */
export const PILOTS_FD_TAB_ID = "flight-duty";
export const PILOTS_FD_TAB_PATH = `/pilots?tab=${PILOTS_FD_TAB_ID}`;

/**
 * Modes only a FLIGHT ADMIN may open — the holder of the `flightduty`
 * `view_all_pilots` permission token (`useIsFlightAdmin`, derived exactly as
 * `canViewAllPilots` in FlightDutyCanduty.tsx). Aaron, round 2 P9: "this mode
 * should only be available with flight admin permissions — pilots dont see it".
 *
 * ── WHY A SEPARATE MAP AND NOT A KEY IN `MODE_LABELS` ─────────────────────
 * `MODE_LABELS` also feeds the SIDEBAR: src/app/navModeRegistry.ts turns it
 * into `subMenus` for the `flightduty` nav row, and that list is static — it
 * carries no per-user permission. A `rules` key there would print "Rules" in
 * every pilot's side nav. So the gated mode lives here: FlightDutyLayout adds
 * it to the breadcrumb dropdown for a flight admin only, and the route itself
 * redirects anyone else (`RulesGate`). A pilot never sees it in either menu.
 */
export const ADMIN_MODE_LABELS: { [segment: string]: string } = {
  rules: "Rules",
};

/**
 * Which modes sit behind which gate — read by the top nav
 * (src/app/navModeRegistry.ts), the breadcrumb (FlightDutyLayout) and the
 * routes (index.tsx). PATTERNS.md BP-90 (H131, 2026-09-28).
 *
 * The "static list" note above is what this ENDS for the top nav: the registry
 * stamps each gated entry with its gate and Menu.tsx renders it only for a user
 * the gate lets in. So a flight admin now finds Rules and Crew Tracker under
 * Pilots → Flight and Duty (Aaron: "missing the rules mode in teh sub menu"),
 * and a pilot finds neither (Aaron: "Pilot group permissions remove these").
 *
 * Crew Tracker is `notPilotsGroup` (H172, Aaron 2026-09-29: "pilots can still
 * see crew tracker - should be just flight admin", ruled "Hide for the Pilots
 * group only"): keyed on 787 it also hid from office staff, who hold it on few
 * tenants. Its gauges read every pilot's duty for anyone but a scoped pilot
 * (useFdScope → seesAllPilotsFor; access.py sees_all_records, H178). Rules stays `flightAdmin` — it WRITES the enabled
 * rules, which the API refuses without 787, and it was 787-only before H131.
 */
export const MODE_GATES: ModeGates = {
  // `tracker: "notPilotsGroup"` (H172) went with the tracker (H212): the Duty
  // log that replaced it shows a pilot only their own banner, so it needs no
  // gate, and keeping one on the old segment would gate a redirect.
  rules: "flightAdmin",
  // P4-DAILY: every pilot's row, so a scoped pilot (who would see only their
  // own) does not get it — H172's gate, `seesAllPilotsFor`, as the route
  // (index.tsx) and the server (access.py) read it.
  summary: "notPilotsGroup",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 *
 * `rules` is here too: ONE map for both label maps. The gate is untouched —
 * FlightDutyLayout only renders the Rules row for a flight admin, and nothing
 * outside the dropdown reads this map, so a pilot never sees the text.
 * `list` is not "every duty record": a pilot-only reader is scoped to their own.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Duty and rest entries",
  canduty: "Pilot limits and days",
  // BP-85: 1-4 words.
  summary: "All pilots, one date",
  rules: "Rule sets in force",
};

// ── `tracker` IS THE NAV ENTRY. THERE IS NO DB ROW TO CHANGE. ──────────────
// Restored 2026-09-04 on Aaron's ruling, having been removed the same morning.
// Worth keeping the mechanism written down, because the loss was first reported
// as a clobbered nav row and diagnosed against `module_g2_demo` on that
// assumption: the tracker was never a `modules` row of its own — it is a
// listing mode of `flightduty` (nav row 10203) — so this key IS the menu item.
// Adding it back here restores the entry, and no live-DB nav mutation was owed
// in either direction.
//
// `isModeSegment` reads this object, so anyone whose localStorage held
// `flightduty.viewMode === "tracker"` through the removal window was falling
// back to `list` rather than 404ing, and now resumes on the tracker.

/**
 * The REGISTER, not Limitations.
 *
 * The register is the mode that reads live data on every tenant; Limitations
 * reads a table that is empty on the tenant this module was built for (0 rows
 * in `flights_pilotroster_duty_limits`, and 0 in both vocabulary tables —
 * re-measured 2026-08-31 pm, after the duty hours themselves were seeded). Opening a
 * module on its least-populated surface teaches an operator less than opening
 * it on its working one, and the switcher makes the other mode one click away.
 *
 * No legacy default-mode config was consulted because none exists: legacy's
 * `flightduty` config carries no `*_default_mode` key (unlike
 * `flight_booking_default_mode`, which is why bookings defaults to Calendar).
 */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * `<slug>.viewMode`, matching every other converted module's key shape.
 *
 * `flightduty` WITHOUT an underscore — see the slug note in the header. The
 * sibling modules whose directory is hyphenated store an UNDERSCORED key
 * (e.g. `tech_log.viewMode`) because their mod_function
 * is underscored. This module's mod_function genuinely has no underscore, so
 * `flightduty.viewMode` follows the same rule and is not an exception to it.
 */
export const MODE_STORAGE_KEY = "flightduty.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx,
 * which carries a row for this module — added WITH this wiring, because
 * bookings' modes.ts cited that file before a row for it existed and the claim
 * was therefore false until someone checked.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment) ||
  Object.prototype.hasOwnProperty.call(ADMIN_MODE_LABELS, segment);

/** The breadcrumb label for any mode segment, gated or not. */
export const modeLabel = (segment: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment)
    ? MODE_LABELS[segment]
    : Object.prototype.hasOwnProperty.call(ADMIN_MODE_LABELS, segment)
      ? ADMIN_MODE_LABELS[segment]
      : undefined;

/**
 * Redirect target for a bare visit to the module root.
 *
 * NO LEGACY-VALUE MIGRATION: this module never shipped a chip+menu switcher
 * (it shipped with no switcher at all), so there is no pre-existing stored
 * value in another shape to translate. Contrast ModificationsLayout, which
 * maps two retired keys forward.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const raw = window.localStorage.getItem(MODE_STORAGE_KEY);
  // A retired mode resumes on its replacement (H212), not on the default.
  const stored =
    raw && Object.prototype.hasOwnProperty.call(RETIRED_MODE_SEGMENTS, raw)
      ? RETIRED_MODE_SEGMENTS[raw]
      : raw;
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base is
 * derived from the LIVE pathname rather than hardcoded, matching every other
 * converted module — App.tsx mounts modules at `menuItem.to + "/*"` straight
 * from the backend nav, so the same module sits at a different path per tenant.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
